// Content plan logic (pure): plan weeks, phases, progress, pillar colours
// and the diff used to save a plan's child rows.
import { addDays, differenceInCalendarDays } from "date-fns";
import type { Identifier } from "ra-core";

import { weekStartOf } from "@/components/market-intel/dates";

import { dayOf, parseDay, pieceDay } from "./calendar";
import { PILLAR_PALETTE } from "./constants";
import type {
  ContentItem,
  ContentPlan,
  ContentPlanChannel,
  ContentPlanPhase,
  ContentPlanPillar,
} from "./types";

/** Week of the plan that contains `day` (1 = week of start_date). */
export const planWeekOf = (plan: ContentPlan, day: string): number | null => {
  if (!plan.start_date) return null;
  const days = differenceInCalendarDays(
    parseDay(weekStartOf(day)),
    parseDay(weekStartOf(plan.start_date)),
  );
  return Math.floor(days / 7) + 1;
};

/** Number of weeks the plan covers (null without both dates). */
export const planWeekCount = (plan: ContentPlan): number | null => {
  if (!plan.start_date || !plan.end_date) return null;
  return planWeekOf(plan, plan.end_date);
};

/** Monday of week `week` of the plan. */
export const planWeekStart = (
  plan: ContentPlan,
  week: number,
): string | null =>
  plan.start_date
    ? dayOf(addDays(parseDay(weekStartOf(plan.start_date)), (week - 1) * 7))
    : null;

/** The phase whose week range contains `week`. */
export const phaseOfWeek = (
  phases: readonly ContentPlanPhase[],
  week: number | null | undefined,
): ContentPlanPhase | null =>
  week == null
    ? null
    : (phases.find(
        (phase) =>
          (phase.start_week ?? 1) <= week &&
          week <= (phase.end_week ?? Number.MAX_SAFE_INTEGER),
      ) ?? null);

export const sortPillars = (
  pillars: readonly ContentPlanPillar[],
): ContentPlanPillar[] =>
  [...pillars].sort(
    (left, right) =>
      (left.position ?? 0) - (right.position ?? 0) ||
      left.code.localeCompare(right.code),
  );

export const sortPhases = (
  phases: readonly ContentPlanPhase[],
): ContentPlanPhase[] =>
  [...phases].sort((left, right) => left.number - right.number);

const HEX = /^#[0-9a-f]{6}$/i;

/** The pillar's colour, or one of the palette by its position in the plan. */
export const pillarColor = (
  pillar: ContentPlanPillar,
  siblings: readonly ContentPlanPillar[],
): string => {
  if (pillar.color && HEX.test(pillar.color)) return pillar.color;
  const index = sortPillars(siblings).findIndex(
    (entry) => String(entry.id) === String(pillar.id),
  );
  return PILLAR_PALETTE[Math.max(0, index) % PILLAR_PALETTE.length];
};

export interface PlanProgress {
  published: number;
  total: number;
  /** posts_per_week × weeks of the plan, over every channel (null if unknown). */
  target: number | null;
  /** published / max(total, target), 0..1. */
  ratio: number;
}

export const planProgress = (
  plan: ContentPlan,
  items: readonly ContentItem[],
  cadences: readonly ContentPlanChannel[],
): PlanProgress => {
  const own = items.filter((item) => String(item.plan_id) === String(plan.id));
  const published = own.filter((item) => item.status === "publicado").length;
  const weeks = planWeekCount(plan);
  const perWeek = cadences
    .filter((cadence) => String(cadence.plan_id) === String(plan.id))
    .reduce((sum, cadence) => sum + Number(cadence.posts_per_week || 0), 0);
  const target = weeks && perWeek ? Math.round(perWeek * weeks) : null;
  const planned = Math.max(own.length, target ?? 0);
  return {
    published,
    total: own.length,
    target,
    ratio: planned ? Math.min(1, published / planned) : 0,
  };
};

/** A plan's pieces grouped by plan week (no week -> 0), in date order. */
export const piecesByWeek = (
  plan: ContentPlan,
  items: readonly ContentItem[],
): { week: number; items: ContentItem[] }[] => {
  const groups = new Map<number, ContentItem[]>();
  for (const item of items) {
    if (String(item.plan_id) !== String(plan.id)) continue;
    const day = pieceDay(item);
    const week = item.week_number ?? (day ? (planWeekOf(plan, day) ?? 0) : 0);
    groups.set(week, [...(groups.get(week) ?? []), item]);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => (left || 999) - (right || 999))
    .map(([week, list]) => ({
      week,
      items: list.sort((a, b) =>
        (a.scheduled_at ?? "").localeCompare(b.scheduled_at ?? ""),
      ),
    }));
};

export interface RowDiff<T> {
  create: Omit<T, "id">[];
  update: { id: Identifier; data: Omit<T, "id">; previousData: T }[];
  remove: Identifier[];
}

/**
 * What to create / update / delete to turn `existing` child rows into
 * `edited` ones (rows without an id are new; `same` skips no-op updates).
 */
export const diffRows = <T extends { id?: Identifier }>(
  existing: readonly (T & { id: Identifier })[],
  edited: readonly T[],
  same: (before: T, after: T) => boolean,
): RowDiff<T> => {
  const kept = new Set(
    edited.filter((row) => row.id != null).map((row) => String(row.id)),
  );
  const result: RowDiff<T> = { create: [], update: [], remove: [] };
  for (const row of edited) {
    const { id, ...data } = row;
    if (id == null) {
      result.create.push(data as Omit<T, "id">);
      continue;
    }
    const before = existing.find((entry) => String(entry.id) === String(id));
    if (before && !same(before, row))
      result.update.push({
        id,
        data: data as Omit<T, "id">,
        previousData: before,
      });
  }
  for (const row of existing)
    if (!kept.has(String(row.id))) result.remove.push(row.id);
  return result;
};
